import React, { useEffect, useState, useRef } from "react";
import { Input, NativeSelect, Tooltip } from "@mui/material";

/**
 * Reusable DataGrid cell component for data_format field
 * Implements debounced state updates to prevent performance issues
 *
 * Features:
 * - Local state for immediate visual feedback
 * - Debounced updates (300ms) to prevent excessive re-renders
 * - Blur handler to flush pending updates
 * - Conditional rendering: text input for non-datetime, select for datetime
 * - Automatic cleanup on unmount
 */
const ResourceDataFormatCell = ({
  params,
  fileData,
  setFileData,
  dateTimeFormat,
}: any) => {
  const [localValue, setLocalValue] = useState(
    fileData?.find((item: any) => item.id === params.row.id)?.data_format || ""
  );
  const debouncedUpdate = useRef<NodeJS.Timeout | null>(null);

  // Sync local state only when row ID changes (not on every fileData change)
  // This prevents flickering caused by useEffect overwriting local state during typing
  useEffect(() => {
    const currentValue =
      fileData?.find((item: any) => item.id === params.row.id)?.data_format ||
      "";
    setLocalValue(currentValue);
  }, [params.row.id]);

  /**
   * Handle change event - update local state immediately and debounce global update
   */
  const handleChange = (event: any) => {
    const value = event.target.value;

    // Update local state immediately for instant feedback
    setLocalValue(value);

    // Clear existing timeout
    if (debouncedUpdate.current) {
      clearTimeout(debouncedUpdate.current);
    }

    // Debounce the update to fileData (300ms delay)
    debouncedUpdate.current = setTimeout(() => {
      setFileData((prev: any) =>
        prev.map((item: any) => {
          if (item.id === params.row.id) {
            return {
              ...item,
              data_format: value,
              changed: true,
            };
          }
          return item;
        })
      );
    }, 300);
  };

  /**
   * Handle blur event - immediately flush pending updates
   */
  const handleBlur = () => {
    if (debouncedUpdate.current) {
      clearTimeout(debouncedUpdate.current);
      setFileData((prev: any) =>
        prev.map((item: any) => {
          if (item.id === params.row.id) {
            return {
              ...item,
              data_format: localValue,
              changed: true,
            };
          }
          return item;
        })
      );
    }
  };

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (debouncedUpdate.current) {
        clearTimeout(debouncedUpdate.current);
      }
    };
  }, []);

  // Check if this row is a datetime type
  const isDatetimeType =
    fileData?.find((item: any) => item.id === params.row.id)?.datatype ===
    "datetime";

  // For datetime types, show a select dropdown; otherwise show text input
  if (isDatetimeType) {
    return (
      <Tooltip
        title={dateTimeFormat.includes(params.value) ? params.value : ""}
        placement="top"
        arrow
      >
        <NativeSelect
          value={dateTimeFormat.includes(localValue) ? localValue : ""}
          className={`white-space-nowrap format-arrow-pos form-control ${
            !dateTimeFormat.includes(localValue) ? "border-red-parent" : ""
          }`}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option key="Select..." value="">
            Select...
          </option>
          {dateTimeFormat.map((format: string) => (
            <option key={format} value={format}>
              {format}
            </option>
          ))}
        </NativeSelect>
      </Tooltip>
    );
  }

  // For non-datetime types, show text input
  return (
    <Input
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={(e: any) => {
        e.stopPropagation();
      }}
      className="form-control input-ellipsis"
    />
  );
};

export default ResourceDataFormatCell;
